<template>
  <app-page class="special-car">
    <car-header :car="car"/>

    <v-row>
      <v-col cols="12" sm="6">
        <car-images :images="car.images"/>
      </v-col>
      <v-col cols="12" sm="6">
        <pre class="car-desc">{{car.description || '車両の説明文を記載します。'}}</pre>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6" v-if="specs && specs.length > 0">
        <h2>主要諸元</h2>
        <car-tablelike :items="specs"/>
      </v-col>
      <v-col cols="12" sm="6" v-if="equipments && equipments.length > 0">
        <h2>装備品</h2>
        <car-tablelike :items="equipments"/>
      </v-col>
    </v-row>

    <h2>予約カレンダー</h2>
    <p>予約開始日を選択して下さい。<br>出発日を含めて3カ月以内の予約できます。</p>
    <car-calendar
      :month="month"
      :monthRange="monthRange"
      :days="days"
      @movemonth="onMoveMonth"
      @selected="onSelected"
    />
  </app-page>
</template>

<style lang="scss">
@import '@/libs/vuetify/styles/variables.scss';

.special-car {
  h2 {
    @extend %typo-title;
    margin: {
      top: 2rem;
      bottom: .5rem;
    }

    &::before {
      color: $table-th-color;
      content: '● ';
    }
  }

  .car-desc {
    white-space: pre-wrap;
  }
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex'
import ShopBase from '@/app/special/mixins/ShopBase'
import CarHeader from './CarHeader'
import CarImages from './CarImages'
import CarTablelike from './CarTablelike'
import CarCalendar from './CarCalendar'

import * as http from '@/libs/http'
import * as time from '@/libs/time'
import * as calendar from '@/app/special/libs/calendar'

const carinit = {
  type: '',
  maker: '',
  name: '',
  images: [],
  description: '',
  specs: [],
  equipments: []
}

export default {
  name: 'Car',
  mixins: [
    ShopBase('車両詳細')
  ],
  components: {
    CarHeader,
    CarImages,
    CarTablelike,
    CarCalendar
  },
  props: {
    carid: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      days: {}
    }
  },
  computed: {
    ...mapGetters('shop', [
      'profile',
      'm_calendar',
      'm_cars'
    ]),

    carID () {
      return this.carid - 0
    },

    car () {
      return this.m_cars.find(item => item.id === this.carID) || carinit
    },

    specs () {
      if (!this.car || !this.car.specs) {
        return []
      }

      // kv に情報を集めると共に、表示フォーマットを整える
      const kv = this.car.specs.reduce((kv, item) => {
        let value = null
        switch (item.name) {
          case '排気量':
            value = `${item.value} cc`
            break
          case '燃料':
            value = item.value
            break
          case '乗車':
            value = `${item.value} 名`
            break
          case '車高':
          case '車幅':
          case '全長':
            value = `${item.value.toLocaleString()} mm`
            break
          case '車重':
            value = `${item.value.toLocaleString()} kg`
            break
        }
        if (value !== null) {
          kv[item.name] = value
        }
        return kv
      }, {})

      return [
        { name: '排気量', value: kv['排気量'] },
        { name: '燃料', value: kv['燃料'] },
        { name: '乗車', value: kv['乗車'] },
        { name: '車重', value: kv['車重'] },
        { name: '全長', value: kv['全長'] },
        { name: '車幅', value: kv['車幅'] },
        { name: '車高', value: kv['車高'] }
      ]
    },

    equipments () {
      if (!this.car || !this.car.equipments) {
        return []
      }

      const kv = this.car.equipments.reduce((kv, item) => {
        kv[item.name] = item.value
        return kv
      }, {})

      return [
        { name: 'ラジオ', value: kv['ラジオ'] },
        { name: 'CD', value: kv.CD },
        { name: 'DVD', value: kv.DVD },
        { name: 'ナビ', value: kv['ナビ'] },
        { name: 'ETC', value: kv.ETC },
        { name: 'ドライブレコーダ', value: kv['ドライブレコーダ'] },
        { name: 'バックモニター', value: kv['バックモニター'] },
        { name: 'パンク修理キット', value: kv['パンク修理キット'] },
        { name: 'スペアタイヤ', value: kv['スペアタイヤ'] },
        { name: 'スタッドレス', value: kv['スタッドレス'] }
      ]
    },

    month () {
      const qm = this.$route.query.m
      if (!qm) {
        return time.format('Y-m-01', new Date())
      }
      const y = qm.slice(0, 4)
      const m = qm.slice(4)
      return `${y}-${m}-01`
    },

    monthRange () {
      const period = this.m_calendar.period

      return {
        first: time.format('Y-m-01', period.begin),
        last: time.format('Y-m-01', period.end)
      }
    }
  },
  created () {
    this.addFetcher(() => this.fetch())
  },
  methods: {
    ...mapActions('book', [
      'newBook'
    ]),

    async fetch () {
      const period = this.m_calendar.period
      const busy = await http.request(async api => {
        const resp = await api.get(`shops/${this.shopID}/busy`, {
          params: {
            carid: [this.car.id],
            s: period.begin,
            e: period.end
          }
        })
        if (resp.status === http.OK) {
          return resp.data[this.car.id]
        }
        if (resp.status === http.NoContent) {
          return []
        }
        return new http.StatusError(resp)
      })
      if (busy === http.FAILED) {
        return false
      }
      const cal = calendar.create(this.m_calendar, 'daysand')
      this.days = cal.daysInfo(busy, this.profile.intervalHour)
      this.pageTitle = this.car.name
      return true
    },

    //
    // 月度が変化した
    //
    onMoveMonth (delta) {
      const month = time.make(this.month)
      month.setMonth(month.getMonth() + delta)

      const m = time.format('Ym', month)
      this.$router.replace({ query: { m } })
    },

    //
    // カレンダーが選択された
    //
    async onSelected (e) {
      // begin を定義する
      const begin = e.period.begin

      // 7 日制約を設定
      const limit = time.clone(begin)
      limit.setDate(limit.getDate() + 7)

      // end を算出する
      const cal = calendar.create(this.m_calendar, 'daysand')
      let end = time.clone(begin)
      end.setDate(end.getDate() + 1)
      const close = cal.testClosed(end, false, true)
      if (!close) {
        // end は営業中
      } else if (close.end <= limit) {
        // close.end は 7 日以内
        end = close.end
      } else {
        // close.end が 7日以上なら、当日の閉店時間
        const d = time.format('Y-m-d', begin)
        end = time.join(d, this.m_calendar.dates[d].end)
      }
      if (e.allow.end < end) {
        end = e.allow.end
      }

      await this.newBook({
        allow: e.allow,
        period: { begin, end },
        carid: this.car.id
      })

      // 予約期間入力へ
      this.$router.push({ name: 'book-period' })
    }
  }
}
</script>
